// 261028 행동 로그 — 손님이 보는 화면·누르는 것·광고 노출·바깥 연결(전화·길찾기·외부 링크)·검색을 모아 몇 초마다 서버로 보낸다(/api/log/actions → tblActionLog).
//   유저 "우리 광고플랫폼이잖아 … 클릭 하나하나가 다 우리한텐 돈이야". 순수 규칙은 actionLogCore.mjs(node 시험), 여기는 화면 연결만.
//   - 수집 스위치 actionLogEnabled(운영 기본 꺼짐, rule 55). 스위치를 받기 전 이벤트는 쥐고 있다가 켜져 있으면 보내고 꺼져 있으면 버린다.
//   - ServiceExec(axios)를 거치지 않는다 — 추적 호출의 401 이 /signin 으로 튀는 가드를 타면 안 된다(260618 retention 사고). 실패는 전부 삼킨다.
//   - 사람 이름·대화가 섞일 수 있는 글자는 싣지 않는다: 아무 상자(div)를 누르면 이름표(data-ev·aria-label)만, 버튼·링크 글자는 한 줄 15자까지.
//   화면에 붙일 수 있는 표식(선택):
//     data-ev="이름"           누른 것 이름(글자 대신)
//     data-ev-id="종류:번호"   누른 대상(예: businessNo:42) — 없으면 지금 화면의 대상
//     data-ev-imp="종류:번호"  화면에 반 넘게 1초 보이면 노출 1번(화면마다 1번) · data-ev-slot="칸 이름" · data-ev-rank="순서"
import store from '@/store';
import { isApp } from '@/common/utils/module/IsMobileSupport';
import { actionLog as actionLogApi } from '@/common/service/apiList';
import {
  anonIdFrom,
  classifyHref,
  createQueue,
  flushDecision,
  maskText,
  parseTarget,
  platformOf,
  routeInfo,
  touchSession,
  FLUSH_SIZE,
  MAX_BATCH
} from '@/common/utils/actionLogCore.mjs';

const FLUSH_MS = 5000;
const RESUME_AFTER_MS = 30 * 1000;
const IMP_VISIBLE_MS = 1000;
const CLICK_DEDUP_MS = 400;
const MAX_EVENTS_PER_LOAD = 2000; // 고장 난 화면이 끝없이 쏘는 것 방어
const ACTIONABLE = 'a[href],button,[role="button"],[data-ev],[data-ev-id],input[type="button"],input[type="submit"],summary';

const queue = createQueue();
let started = false;
let count = 0;
let anonId = null;
let sessionId = null;
let current = { page: null, targetType: null, targetId: null, props: {} };
let pageAt = Date.now();
let shownAt = Date.now();
let hiddenAt = 0;
let lastClick = { el: null, at: 0 };
let pendingClick = null; // 캡처 단계에서 잡은 누름 — 다음 차례(setTimeout 0)에 CLICK 으로 넣는다
let outboundLogged = false; // 이번 누름을 바깥 연결 링크로 이미 셌다
let impSeen = new Set();

const commitPendingClick = () => {
  const p = pendingClick;
  pendingClick = null;
  if (p) logAction('CLICK', p);
};

const makeId = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  } catch {
    /* 아래 대체 */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const appInfo = () => {
  let app = false;
  try {
    app = isApp();
  } catch {
    app = false;
  }
  const dev = store.getters['user/getDeviceInfo'] || {};
  // 앱이 기기 정보(nativeSetDeviceInfo)를 넘기기 전엔 store 기본값(deviceId 'WEB'·appVersion '1.0.0')이다 — 그땐 버전을 모른다(null).
  //   안드로이드는 켜고 1~5초 뒤에 넘긴다(푸시 토큰 기다림). 261028 운영에서 첫 기록들이 '1.0.0' 으로 찍혔다.
  const known = !!dev.deviceId && dev.deviceId !== 'WEB';
  return { platform: platformOf(app, navigator.userAgent), appVersion: app && known ? dev.appVersion || null : null };
};

const touch = () => {
  try {
    sessionId = touchSession(window.localStorage, Date.now(), makeId).id;
  } catch {
    sessionId = sessionId || makeId();
  }
};

/** 이벤트 하나를 줄에 넣는다. type = APP_OPEN·APP_RESUME·APP_HIDE·PAGE_VIEW·CLICK·OUTBOUND·IMPRESSION·SEARCH(서버 목록과 같다). */
export const logAction = (type, fields = {}) => {
  if (!started || count >= MAX_EVENTS_PER_LOAD) return;
  try {
    touch();
    const { platform, appVersion } = appInfo();
    queue.push({
      type,
      anonId,
      sessionId,
      platform,
      appVersion,
      page: fields.page !== undefined ? fields.page : current.page,
      refPage: fields.refPage || null,
      targetType: fields.targetType !== undefined ? fields.targetType : current.targetType,
      targetId: fields.targetId !== undefined ? fields.targetId : current.targetId,
      label: fields.label || null,
      val: Number.isFinite(fields.val) && fields.val >= 0 ? Math.round(fields.val) : null,
      props: fields.props && Object.keys(fields.props).length ? fields.props : null
    });
    count++;
    if (queue.size >= FLUSH_SIZE) flush();
  } catch {
    /* 분석 데이터 — 무시 */
  }
};

/** 업체 검색 — 3초 안에 이어 친 검색어는 마지막 하나로 합쳐진다(actionLogCore.createQueue). */
export const logSearch = (query, resultCount) => {
  const label = maskText(query, 60);
  if (!label) return;
  logAction('SEARCH', { label, val: Number.isFinite(resultCount) ? resultCount : null });
};

const send = (events, keepalive) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = store.getters['user/getAccessToken'];
  if (token) headers.authorization = token;
  try {
    fetch(actionLogApi.collect, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      keepalive: !!keepalive,
      credentials: 'same-origin'
    }).catch(() => {});
  } catch {
    /* fetch 없음·막힘 — 무시 */
  }
};

/** 쌓인 것을 보낸다. 화면을 떠날 때(keepalive)는 페이지가 닫혀도 요청이 끝까지 가게. */
export const flush = (keepalive = false) => {
  try {
    if (!queue.size) return;
    const d = flushDecision(store.getters['common/isFeatureFlagsLoaded'], store.getters['common/getFeatureFlags']);
    if (d === 'wait') return;
    if (d === 'drop') {
      queue.clear();
      return;
    }
    while (queue.size) send(queue.take(MAX_BATCH), keepalive);
  } catch {
    /* 무시 */
  }
};

const openProps = () => {
  const p = {};
  try {
    const ref = document.referrer ? new URL(document.referrer).hostname : '';
    if (ref && ref !== window.location.hostname) p.ref = ref;
  } catch {
    /* 무시 */
  }
  try {
    const utm = window.localStorage.getItem('jump_utm_source'); // App.vue 가 진입 때 남긴 유입 경로
    if (utm) p.utm = maskText(utm, 40);
  } catch {
    /* 무시 */
  }
  return p;
};

const onRoute = (to, from, failure) => {
  if (failure || !from || !from.matched || !from.matched.length) return; // 첫 화면은 init 이 따로 센다
  if (to.fullPath === from.fullPath) return;
  const now = Date.now();
  const prevPage = current.page;
  const dwell = now - pageAt;
  current = routeInfo(to);
  impSeen = new Set();
  pageAt = now;
  logAction('PAGE_VIEW', { refPage: prevPage, val: dwell, props: current.props });
};

const onVisibility = () => {
  const now = Date.now();
  if (document.visibilityState === 'hidden') {
    logAction('APP_HIDE', { val: now - shownAt, props: { stay: now - pageAt } });
    hiddenAt = now;
    flush(true);
  } else if (document.visibilityState === 'visible') {
    shownAt = now;
    if (hiddenAt && now - hiddenAt >= RESUME_AFTER_MS) logAction('APP_RESUME', { val: now - hiddenAt });
    hiddenAt = 0;
  }
};

// 아무 상자(div)라도 손가락 모양이면 누르는 곳 — 5단계까지 올라가 본다
const pointerAncestor = node => {
  let n = node;
  for (let i = 0; n && i < 5 && n !== document.body; i++, n = n.parentElement) {
    try {
      if (window.getComputedStyle(n).cursor === 'pointer') return n;
    } catch {
      return null;
    }
  }
  return null;
};

const elementTag = el => {
  const cls = el.classList && el.classList.length ? '.' + el.classList[0] : '';
  return maskText(el.tagName.toLowerCase() + cls, 40);
};

const elementLabel = (el, generic) => {
  const named = el.getAttribute('data-ev') || el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('alt');
  if (named) return maskText(named, 30);
  if (generic) return null; // 상자 글자엔 사람 이름·대화가 섞일 수 있다
  const text = (el.innerText || el.textContent || '').trim();
  if (text && !text.includes('\n') && Array.from(text).length <= 15) return maskText(text, 15);
  const img = !text && el.querySelector ? el.querySelector('img[alt]') : null;
  return img ? maskText(img.getAttribute('alt'), 30) : null;
};

const onClick = e => {
  try {
    const t = e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement;
    if (!t || !t.closest) return;
    let el = t.closest(ACTIONABLE);
    const generic = !el;
    if (!el) el = pointerAncestor(t);
    if (!el) return;
    const now = Date.now();
    if (lastClick.el === el && now - lastClick.at < CLICK_DEDUP_MS) return;
    lastClick = { el, at: now };
    const evId = el.getAttribute('data-ev-id');
    const tgt = evId ? parseTarget(evId) : { targetType: current.targetType, targetId: current.targetId };
    const label = elementLabel(el, generic);
    const props = { el: elementTag(el) };
    const slot = el.closest('[data-ev-slot]');
    if (slot) props.slot = maskText(slot.getAttribute('data-ev-slot'), 24);
    const out = el.tagName === 'A' ? classifyHref(el.href || el.getAttribute('href'), window.location.href) : null;
    commitPendingClick();
    if (out) {
      if (out.host) props.host = out.host;
      if (label) props.text = label;
      logAction('OUTBOUND', { ...tgt, label: out.kind, props });
      outboundLogged = true; // 이 링크의 @click 이 logOutbound 를 또 불러도 두 번 세지 않게
      setTimeout(() => (outboundLogged = false), 0);
    } else {
      // 화면 코드(@click)는 이 캡처 단계 뒤에 돈다 — 그 안에서 logOutbound 가 불리면 이 누름이 바깥 연결 1건이 된다
      pendingClick = { ...tgt, page: current.page, label, props };
      setTimeout(commitPendingClick, 0);
    }
  } catch {
    /* 무시 */
  }
};

/**
 * 화면 코드가 직접 바깥으로 보낼 때(location.href = 'tel:…'·지도 앱·외부 주소, window.open) 그 바로 앞에서 부른다.
 * 같은 누름 안이면 그 누름(대상·글자·칸)을 바깥 연결 1건으로 바꾸고, 아니면 지금 화면 대상으로 1건. 같은 사이트 안 주소는 세지 않는다.
 * 261028 업체 화면 '전화걸기'(nativeTel)·현판 '전화 주문·예약' 이 '전화' 대신 '누름'으로만 남던 것을 고친다.
 */
export const logOutbound = href => {
  try {
    const out = classifyHref(href, window.location.href);
    if (!out || outboundLogged) return;
    const base = pendingClick || { targetType: current.targetType, targetId: current.targetId, page: current.page, label: null, props: {} };
    pendingClick = null;
    const props = { ...base.props };
    if (out.host) props.host = out.host;
    if (base.label) props.text = base.label;
    logAction('OUTBOUND', { targetType: base.targetType, targetId: base.targetId, page: base.page, label: out.kind, props });
  } catch {
    /* 분석 데이터 — 무시 */
  }
};

/** `window.location.href = 주소` 로 바깥에 나가는 화면 코드용 — 센 뒤 그 주소로 보낸다(동작은 그대로). */
export const goOutbound = href => {
  logOutbound(href);
  window.location.href = href;
};

const impTimers = new WeakMap();
const impression = el => {
  if (!el.isConnected) return;
  const key = el.getAttribute('data-ev-imp');
  if (!key) return;
  const seenKey = current.page + '|' + key;
  if (impSeen.has(seenKey)) return;
  impSeen.add(seenKey);
  const props = {};
  const slotEl = el.closest('[data-ev-slot]');
  if (slotEl) props.slot = maskText(slotEl.getAttribute('data-ev-slot'), 24);
  const rank = Number(el.getAttribute('data-ev-rank'));
  if (Number.isFinite(rank) && rank > 0) props.rank = rank;
  logAction('IMPRESSION', { ...parseTarget(key), label: maskText(el.getAttribute('data-ev-label'), 30), props });
};

const observeImpressions = () => {
  if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;
  const io = new IntersectionObserver(
    entries => {
      for (const en of entries) {
        const el = en.target;
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          if (!impTimers.has(el)) {
            impTimers.set(
              el,
              setTimeout(() => {
                impTimers.delete(el);
                impression(el);
              }, IMP_VISIBLE_MS)
            );
          }
        } else if (impTimers.has(el)) {
          clearTimeout(impTimers.get(el));
          impTimers.delete(el);
        }
      }
    },
    { threshold: [0, 0.5] }
  );
  const scan = () => {
    document.querySelectorAll('[data-ev-imp]:not([data-ev-obs])').forEach(el => {
      el.setAttribute('data-ev-obs', '1');
      io.observe(el);
    });
  };
  scan();
  let pending = null;
  new MutationObserver(() => {
    if (pending) return;
    pending = setTimeout(() => {
      pending = null;
      scan();
    }, 500);
  }).observe(document.body, { childList: true, subtree: true });
};

/** App.vue onMounted 에서 한 번. 첫 화면·앱 열림을 세고, 화면 이동·누름·노출·앱 전환을 듣기 시작한다. */
export const initActionLog = router => {
  if (started || typeof window === 'undefined' || !router) return;
  started = true;
  try {
    anonId = anonIdFrom(window.localStorage, makeId);
  } catch {
    anonId = null;
  }
  try {
    router.afterEach(onRoute);
    router
      .isReady()
      .then(() => {
        current = routeInfo(router.currentRoute.value);
        pageAt = Date.now();
        logAction('APP_OPEN', { props: openProps() });
        logAction('PAGE_VIEW', { props: current.props });
      })
      .catch(() => {});
    document.addEventListener('click', onClick, { capture: true, passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', () => flush(true));
    window.setInterval(() => flush(false), FLUSH_MS);
    observeImpressions();
  } catch {
    /* 무시 — 기록이 안 될 뿐 화면은 그대로 */
  }
};
